import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ConnectedAccountsList } from "Components/Banking/AccountSelectionDialog/ConnectedAccountsList";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import type { BankAccountListItem } from "Infrastructure/Api/Api";

const RESOURCES = Resources.Banking.AccountSelection;

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedAccountIds: Array<number>) => void;
  accounts: Array<BankAccountListItem>;
  bankName: string;
  isLoading?: boolean;
  isPending: boolean;
};

export const AccountSelectionDialog = ({
  open,
  onClose,
  onConfirm,
  accounts,
  bankName,
  isLoading = false,
  isPending = false,
}: Props) => {
  const [selectedAccountIds, setSelectedAccountIds] = useState<Array<number>>(
    () => accounts.map((account) => account.bankAccountID),
  );
  const { t } = useResource();

  const handleConfirm = () => {
    onConfirm(selectedAccountIds);
  };

  const handleAccountToggle = (accountId: number) => {
    setSelectedAccountIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId],
    );
  };

  const allAccountIds = accounts.map((account) => account.bankAccountID);
  const allSelected =
    accounts.length > 0 && selectedAccountIds.length === allAccountIds.length;
  const someSelected =
    selectedAccountIds.length > 0 &&
    selectedAccountIds.length < allAccountIds.length;

  const handleSelectAllToggle = () =>
    allSelected
      ? setSelectedAccountIds([])
      : setSelectedAccountIds(allAccountIds);

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={onClose}
      maxWidth="sm"
      slotProps={{ paper: { sx: { borderRadius: 2 } } }}
    >
      <DialogTitle>{t(RESOURCES.Title, { bankName })}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Typography variant="body2" color="text.secondary">
            {t(RESOURCES.Description, { bankName })}
          </Typography>

          {isLoading ? (
            <CenteredCircularProgress />
          ) : (
            <Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAllToggle}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500}>
                    {someSelected
                      ? t(RESOURCES.SelectedAccounts)
                      : t(RESOURCES.AllAccounts)}
                  </Typography>
                }
              />
              <ConnectedAccountsList
                accounts={accounts}
                selectedAccountIds={selectedAccountIds}
                onAccountChange={handleAccountToggle}
              />
            </Stack>
          )}
          <Typography
            variant="body2"
            color="warning.dark"
            display="flex"
            alignItems="flex-start"
            gap={1}
            sx={{ mb: 1 }}
          >
            <WarningAmberOutlinedIcon
              sx={{
                height: "1rem",
                width: "1rem",
                mt: 0.5,
                color: "warning.dark",
              }}
            />
            {t(RESOURCES.WarningMessage)}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            display="flex"
            alignItems="flex-start"
            gap={1}
          >
            <InfoOutlinedIcon sx={{ height: "1rem", width: "1rem", mt: 0.5 }} />
            {t(RESOURCES.DeleteLaterInfo)}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: "16px 24px" }}>
        <Button
          fullWidth
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: "none" }}
          disabled={isPending}
        >
          {t(RESOURCES.Cancel)}
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleConfirm}
          sx={{ textTransform: "none" }}
          loading={isPending}
        >
          {t(RESOURCES.Confirm)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
